import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/authContext';
import UpgradePrompt from './UpgradePrompt';
import LanguageSelector from './LanguageSelector';
import OfflineStatusBadge from './OfflineStatusBadge';
import { PWADownloadIcon } from './PWADownloadIcon';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useQueryClient } from '@tanstack/react-query';
import { useOfflineSync } from '../hooks/useOfflineSync';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const Header: React.FC = () => {
    const { user, isAuthenticated, logout } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    // Init the hook to start listening to SW events
    useOfflineSync();

    useEffect(() => {
        const handleSyncComplete = (event: Event) => {
            const customEvent = event as CustomEvent;
            const { summary } = customEvent.detail;

            console.log('[Header] Received smart-sync-complete event', customEvent.detail);

            if (summary?.affectedLists?.length > 0) {
                console.log('[Header] Invalidating shopping-lists queries');
                queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
                // Removed snackbar - just log the success
                const totalChanges = (summary.itemsAdded || 0) + (summary.itemsToggled || 0) + (summary.itemsDeleted || 0);
                console.log(`✅ Sync completed: ${totalChanges} changes synced`);
            }
        };

        window.addEventListener('smart-sync-complete', handleSyncComplete);

        return () => {
            window.removeEventListener('smart-sync-complete', handleSyncComplete);
        };
    }, [queryClient]);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const handleAccountSettings = () => {
        navigate('/account-settings');
    };

    return (
        <header className="fixed top-0 left-0 right-0 z-50 w-full bg-white/90 backdrop-blur-md border-b border-gray-200/50 shadow-sm">
            <div className="container mx-auto flex items-center justify-between px-4 py-3 min-h-[64px]">
                <div
                    role="button"
                    tabIndex={0}
                    className="flex items-center gap-3 cursor-pointer transition-all duration-200 hover:scale-105 px-2 py-1 rounded-lg hover:bg-gray-50"
                    onClick={() => navigate(isAuthenticated ? '/app' : '/')}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(isAuthenticated ? '/app' : '/'); }}
                >
                    <img
                        src="/minifil-header.png"
                        alt="Minifil Logo"
                        className="h-10 w-auto"
                    />
                    <span className="text-xl font-bold text-gray-800 hidden sm:block mansalva-regular">
                        {t('app.title')}
                    </span>
                </div>

                {/* Elegant offline status in the middle */}
                <div className="flex-1 flex justify-center">
                    <OfflineStatusBadge />
                </div>

                <div className="flex items-center gap-3 sm:gap-4">
                    {/* PWA Download Icon - Subtle promotion */}
                    <PWADownloadIcon size="sm" />
                    {isAuthenticated && <UpgradePrompt />}
                    {isAuthenticated ? (
                        <div className="flex items-center gap-3 sm:gap-4">
                            <span className="text-gray-700 hidden sm:block font-medium text-sm">
                                {user?.name}
                            </span>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="rounded-full hover:bg-gray-100">
                                        <Avatar className="h-8 w-8">
                                            <AvatarFallback className="text-sm">{user?.name?.charAt(0)}</AvatarFallback>
                                        </Avatar>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                    <DropdownMenuItem onClick={handleAccountSettings} className="cursor-pointer">
                                        <span className="mr-2">⚙️</span> {t('account.settings')}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                                        <span className="mr-2">👋</span> {t('auth.logout')}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    ) : (
                        <Button
                            variant="outline"
                            onClick={() => navigate('/login')}
                            className="border-gray-300 hover:bg-gray-50"
                        >
                            🔑 {t('auth.login')}
                        </Button>
                    )}
                    <LanguageSelector />
                </div>
            </div>
        </header>
    );
};

export default Header;
